import { apiUrl } from './platform'

const MAX_RETRIES = 2
const INITIAL_DELAY_MS = 1000

function isRetryable(status) {
  return status === 429 || status >= 500
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Calls the server-side API to generate a daily schedule using AI.
 * Retries up to 2 times on 429/5xx errors with exponential backoff.
 * Returns an array of schedule items.
 * Throws on failure — caller should handle fallback.
 */
export async function fillSchedule({ classes, calendarEvents, availableSubtasks, lockedItems, dayStart, dayEnd }) {
  let lastError

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await delay(INITIAL_DELAY_MS * attempt)
    }

    const response = await fetch(apiUrl('/api/fill-schedule'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ classes, calendarEvents, availableSubtasks, lockedItems, dayStart, dayEnd }),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      lastError = new Error(err.error || `API error: ${response.status}`)
      lastError.status = response.status

      if (isRetryable(response.status) && attempt < MAX_RETRIES) {
        continue
      }
      throw lastError
    }

    const data = await response.json()

    if (!Array.isArray(data.schedule) || data.schedule.length === 0) {
      throw new Error('No schedule returned')
    }

    return data.schedule
  }

  throw lastError
}

/**
 * Calls the server-side API to modify a schedule via chat.
 * Returns { type: 'schedule'|'question', schedule: [...]|null, message: '...' }
 */
export async function chatSchedule({ userMessage, currentSchedule, availableSubtasks, classes, calendarEvents, dayStart, dayEnd, chatHistory }) {
  let lastError

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (attempt > 0) {
      await delay(INITIAL_DELAY_MS * attempt)
    }

    const response = await fetch(apiUrl('/api/chat-schedule'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userMessage, currentSchedule, availableSubtasks, classes, calendarEvents, dayStart, dayEnd, chatHistory }),
    })

    if (!response.ok) {
      const err = await response.json().catch(() => ({}))
      lastError = new Error(err.error || `API error: ${response.status}`)
      lastError.status = response.status

      if (isRetryable(response.status) && attempt < MAX_RETRIES) {
        continue
      }
      throw lastError
    }

    const data = await response.json()

    if (!data || !data.type || !data.message) {
      throw new Error('Invalid response from AI')
    }

    return data
  }

  throw lastError
}
